import React, { useState, useEffect, useCallback, useRef } from 'react';

// Read-Card modal — "what's on this card?". Opens from PmsRoomRack (or
// anywhere else a front-desk user needs to diagnose a card). Fires
// POST /api/v1/lock/read against the property's online agent, polls the
// request status, and shows the structured LT response.
//
// Props:
//   token, onClose
export default function LockReadCardModal({ token, onClose }) {
  const [agents, setAgents] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [encoderId, setEncoderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const pollRef = useRef(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'
  }), [token]);

  useEffect(() => {
    (async () => {
      try {
        const [vRes, aRes] = await Promise.all([
          fetch('/api/v1/lock/vendors', { headers: headers() }),
          fetch('/api/v1/lock/agents',  { headers: headers() })
        ]);
        const v = await vRes.json();
        const a = await aRes.json();
        if (!aRes.ok) throw new Error(a.error || 'Failed to load');
        setVendors(v.vendors || []);
        setAgents(a.agents || []);
        const online = (a.agents || []).find((g) => g.status === 'ONLINE');
        const reserved = (v.vendors || []).find((vv) => vv.vendor_key === online?.vendor)?.reserved_encoder_ids || [];
        const firstEncoder = Array.isArray(online?.encoders_json)
          ? online.encoders_json.find((e) => !reserved.includes(Number(e.id ?? e.Id)))
          : null;
        if (firstEncoder) setEncoderId(Number(firstEncoder.id ?? firstEncoder.Id));
      } catch (e) {
        setError(e.message || 'Load failed');
      } finally { setLoading(false); }
    })();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [headers]);

  const onlineAgents = agents.filter((a) => a.status === 'ONLINE');
  const noOnline = onlineAgents.length === 0;

  const submit = async () => {
    if (!encoderId) return setError('Pick an encoder');
    setSubmitting(true); setError(''); setRequest(null);
    try {
      const res = await fetch('/api/v1/lock/read', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ encoder_id: encoderId, card_eject: 'E' })
      });
      const data = await res.json();
      if (res.status === 503) { setError('Lock system offline.'); return; }
      if (!res.ok) throw new Error(data.error || 'Read failed');
      setRequest({ request_id: data.request_id, status: data.status || 'SENT' });
      schedulePoll(data.request_id);
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  const schedulePoll = (requestId) => {
    if (pollRef.current) clearTimeout(pollRef.current);
    pollRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/v1/lock/requests/${requestId}`, { headers: headers() });
        const data = await res.json();
        if (res.ok && data.request) {
          setRequest(data.request);
          if (!['DONE', 'ERROR', 'TIMEOUT'].includes(data.request.status)) schedulePoll(requestId);
        } else schedulePoll(requestId);
      } catch { schedulePoll(requestId); }
    }, 1500);
  };

  const card = request?.card_read_json || request?.normalized_result_json;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div style={overlay}>
      <div style={dialog}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: '#1e293b' }}>Read Card</h3>
          <button onClick={onClose} style={closeX}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading…</div>
        ) : noOnline ? (
          <div style={{ background: '#fef3c7', color: '#92400e', padding: 12, borderRadius: 6, border: '1px solid #fcd34d' }}>
            <b>Lock system offline.</b> No agent online — read the card on the Onity terminal directly.
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10, alignItems: 'center', marginBottom: 12 }}>
              <label style={lbl}>Encoder</label>
              <select style={inp} value={encoderId || ''} onChange={(e) => setEncoderId(Number(e.target.value))}>
                <option value="">— select —</option>
                {onlineAgents.flatMap((a) => {
                  const reserved = vendors.find((v) => v.vendor_key === a.vendor)?.reserved_encoder_ids || [];
                  return (Array.isArray(a.encoders_json) ? a.encoders_json : []).map((enc) => {
                    const id = Number(enc.id ?? enc.Id);
                    if (reserved.includes(id)) return null;
                    return <option key={`${a.agent_id}-${id}`} value={id}>{a.display_name} — Encoder {id}</option>;
                  }).filter(Boolean);
                })}
              </select>
            </div>

            {error && <div style={errBox}>{error}</div>}

            {!request && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button onClick={onClose} style={btnSecondary}>Cancel</button>
                <button onClick={submit} disabled={submitting} style={btnPrimary}>
                  {submitting ? 'Sending…' : 'Read Card'}
                </button>
              </div>
            )}

            {request && (
              <>
                <div style={{ marginBottom: 12, fontSize: '0.875rem', color: '#475569' }}>
                  Status: <b>{request.status}</b>
                  {request.status === 'PENDING' && ' — waiting…'}
                  {request.status === 'SENT' && ' — sent to lock system…'}
                  {request.status === 'ACK' && ' — insert a card to read…'}
                </div>

                {request.status === 'DONE' && card && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12 }}>
                    <KV k="Card type"      v={cardKindLabel(card.card_kind)} />
                    <KV k="Rooms"          v={(card.rooms || []).filter(Boolean).join(', ') || '—'} />
                    <KV k="Copy"           v={copyLabel(card.copy_no)} />
                    <KV k="Authorisations" v={(card.auth_grant || []).length > 0 ? card.auth_grant.join(', ') : '—'} />
                    <KV k="Valid from"     v={fmtDate(card.valid_from)} />
                    <KV k="Valid until"    v={fmtDate(card.valid_until)} />
                    <KV k="Issued by"      v={card.operator_audit || '—'} />
                  </div>
                )}

                {(request.status === 'ERROR' || request.status === 'TIMEOUT') && (
                  <div style={errBox}>
                    <b>{normalizedErrorMessage(request.normalized_error)}</b>
                    {request.vendor_error_code && (
                      <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#7f1d1d' }}>
                        Vendor code: {request.vendor_error_code}
                        {request.vendor_error_message ? ` — ${request.vendor_error_message}` : ''}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                  {['DONE', 'ERROR', 'TIMEOUT'].includes(request.status) && (
                    <button onClick={() => { setRequest(null); setError(''); }} style={btnSecondary}>Read another</button>
                  )}
                  <button onClick={onClose} style={btnPrimary}>Close</button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={{ display: 'flex', padding: '5px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.875rem' }}>
      <div style={{ width: 140, color: '#64748b' }}>{k}</div>
      <div style={{ flex: 1, color: '#0f172a' }}>{v}</div>
    </div>
  );
}

function cardKindLabel(kind) {
  return ({
    guest_valid:   'Guest card — valid',
    checked_out:   'Guest card — already checked out',
    guest_invalid: 'Guest card — invalid',
    master:        'Master card / special card',
    spare:         'Spare card (backup)',
    diagnostic:    'Diagnostic card',
    foreign:       'Card belongs to another property',
    empty:         'No card detected'
  }[kind] || kind || '—');
}

function copyLabel(c) {
  if (!c) return '—';
  if (c === 'A') return 'One-shot card';
  if (c === 'I') return 'Copy #5+ (undefined)';
  if (c === '0' || c === 0) return 'Original';
  return `Copy #${c}`;
}

function normalizedErrorMessage(code) {
  return ({
    CARD_BACKWARD: 'Card inserted the wrong way. Flip and reinsert.',
    CARD_NOT_INSERTED: 'No card detected.',
    CARD_DAMAGED: 'Card unreadable.',
    CARD_JAMMED: 'Card jammed in encoder.',
    TRANSPORT_DOWN: 'Lock system offline.',
    ENCODER_OFFLINE: 'Encoder not responding.',
    ENCODER_TIMEOUT: 'Encoder timed out.',
    PROTOCOL_ERROR: 'Communication error.'
  }[code] || `Read failed (${code || 'unknown'}).`);
}

const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.55)', zIndex: 1500,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
};
const dialog = {
  background: '#fff', borderRadius: 10, padding: 22,
  maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 24px 60px rgba(15,23,42,0.25)'
};
const closeX = { background: 'none', border: 'none', fontSize: 22, color: '#64748b', cursor: 'pointer' };
const lbl = { color: '#475569', fontSize: '0.875rem', fontWeight: 500 };
const inp = { padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const errBox = { marginTop: 12, background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6 };
const btnPrimary   = { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' };
